import React, { useState } from 'react';
import {
    Box, Container, Typography, Grid, Paper, TextField, Button, Alert,
    Stack, Card, CardContent, Avatar, useTheme, useMediaQuery, CircularProgress
} from '@mui/material';
import {
    Email, Phone, LocationOn, Send,
    Feedback, BugReport, Help
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import BackgroundShapes from '../components/common/BackgroundShapes';

const ContactUsPage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: '',
        website: '' // Honeypot field for spam protection
    });
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: formData.name,
                    email: formData.email,
                    subject: formData.subject,
                    message: formData.message,
                    website: formData.website // Honeypot field - should always be empty
                })
            });

            const result = await response.json();

            if (response.ok && result.success) {
                setSuccess(result.message || 'Thank you for your message! We\'ll get back to you soon.');
                setSubmitted(true);

                // Reset form after successful submission
                setFormData({
                    name: '',
                    email: '',
                    subject: '',
                    message: '',
                    website: ''
                });

                // Reset success message after 5 seconds
                setTimeout(() => {
                    setSubmitted(false);
                    setSuccess('');
                }, 5000);
            } else {
                // Handle different error scenarios
                if (response.status === 429) {
                    setError('Please wait a moment before sending another message.');
                } else {
                    setError(result.message || 'Something went wrong. Please try again later.');
                }
            }
        } catch (err) {
            console.error('Error submitting contact form:', err);
            setError('Network error. Please check your connection and try again.');
        } finally {
            setLoading(false);
        }
    };

    const contactMethods = [
        {
            icon: <Email sx={{ fontSize: { xs: 32, sm: 40 }, color: '#43a047' }} />,
            title: 'Email Us',
            description: 'Get in touch via email for detailed inquiries',
            contact: 'support@sortifi.org',
            action: 'mailto:support@sortifi.org?subject=Sortify%20Contact&body=Message%20goes%20here'
        },
        {
            icon: <Phone sx={{ fontSize: { xs: 32, sm: 40 }, color: '#43a047' }} />,
            title: 'Call Us',
            description: 'Speak directly with our support team',
            contact: '+880 1822-679672',
            action: 'tel:+8801822679672'
        },
        {
            icon: <LocationOn sx={{ fontSize: { xs: 32, sm: 40 }, color: '#43a047' }} />,
            title: 'Visit Us',
            description: 'Meet us at our university campus',
            contact: 'IUBAT, Dhaka, Bangladesh',
            action: 'https://maps.app.goo.gl/Zjkuxt8bVxT7Kbai6'
        }
    ];

    const supportTypes = [
        {
            icon: <Help />,
            title: 'General Support',
            description: 'Questions about using Sortify'
        },
        {
            icon: <BugReport />,
            title: 'Bug Reports',
            description: 'Found an issue? Let us know!'
        },
        {
            icon: <Feedback />,
            title: 'Feedback',
            description: 'Share your thoughts and suggestions'
        }
    ];

    return (
        <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f8faf8 0%, #e6f4ea 100%)' }}>
            <BackgroundShapes variant="cross" opacity={0.03} />

            {/* Hero Section - Mobile Responsive */}
            <Box sx={{
                background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                color: 'white',
                py: { xs: 4, sm: 6, md: 8 }
            }}>
                <Container maxWidth="lg">
                    <Grid container spacing={{ xs: 3, sm: 4 }} alignItems="center">
                        <Grid item xs={12} md={8} order={{ xs: 2, md: 1 }}>
                            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                                <Typography
                                    variant="h2"
                                    fontWeight={800}
                                    gutterBottom
                                    sx={{
                                        fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3rem' },
                                        lineHeight: { xs: 1.2, sm: 1.3 }
                                    }}
                                >
                                    📞 Contact Us
                                </Typography>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        opacity: 0.9,
                                        mb: { xs: 3, sm: 4 },
                                        fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' },
                                        lineHeight: { xs: 1.3, sm: 1.4 },
                                        px: { xs: 1, sm: 0 }
                                    }}
                                >
                                    We'd love to hear from you! Get in touch with The IUBAT Duo behind Sortify.
                                </Typography>
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={{ xs: 1, sm: 2 }}
                                    flexWrap="wrap"
                                    alignItems={{ xs: 'center', sm: 'flex-start' }}
                                    justifyContent={{ xs: 'center', md: 'flex-start' }}
                                >
                                    <Box sx={{
                                        background: 'rgba(255,255,255,0.2)',
                                        color: 'white',
                                        px: { xs: 2, sm: 2 },
                                        py: 1,
                                        borderRadius: 2,
                                        fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                    }}>
                                        🏆 JavaFest 2025 Project
                                    </Box>
                                    <Box sx={{
                                        background: 'rgba(255,255,255,0.2)',
                                        color: 'white',
                                        px: { xs: 2, sm: 2 },
                                        py: 1,
                                        borderRadius: 2,
                                        fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                    }}>
                                        🇧🇩 Made in Bangladesh
                                    </Box>
                                </Stack>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4} order={{ xs: 1, md: 2 }}>
                            <Box sx={{
                                textAlign: 'center',
                                fontSize: { xs: '4rem', sm: '6rem', md: '8rem' }
                            }}>
                                💬
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            <Container maxWidth="lg" sx={{ py: { xs: 6, sm: 8 } }}>
                {/* Contact Methods - Mobile Responsive */}
                <Grid container spacing={{ xs: 3, sm: 4 }} sx={{ mb: { xs: 6, sm: 8 } }}>
                    {contactMethods.map((method, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <Card sx={{
                                height: '100%',
                                textAlign: 'center',
                                borderRadius: { xs: 3, sm: 4 },
                                background: 'linear-gradient(135deg, #ffffff, #f5fbf7)',
                                border: '2px solid #43a04715',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: { xs: 'none', sm: 'translateY(-8px)' },
                                    boxShadow: '0 16px 48px rgba(67, 160, 71, 0.15)',
                                    borderColor: '#43a047'
                                }
                            }}>
                                <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                                    <Avatar sx={{
                                        width: { xs: 60, sm: 80 },
                                        height: { xs: 60, sm: 80 },
                                        background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                                        mx: 'auto',
                                        mb: { xs: 2, sm: 3 }
                                    }}>
                                        {method.icon}
                                    </Avatar>
                                    <Typography
                                        variant="h5"
                                        fontWeight={700}
                                        gutterBottom
                                        color="#1b5e20"
                                        sx={{ fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.5rem' } }}
                                    >
                                        {method.title}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="#546e7a"
                                        paragraph
                                        sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}
                                    >
                                        {method.description}
                                    </Typography>
                                    <Typography
                                        variant="h6"
                                        fontWeight={600}
                                        color="#43a047"
                                        gutterBottom
                                        sx={{
                                            fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' },
                                            wordBreak: 'break-word'
                                        }}
                                    >
                                        {method.contact}
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        component="a"
                                        href={method.action}
                                        target={method.action.startsWith('http') ? '_blank' : undefined}
                                        sx={{
                                            mt: 2,
                                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                            px: { xs: 2, sm: 3 }
                                        }}
                                    >
                                        Contact Now
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* Contact Form - Mobile Responsive */}
                <Grid container spacing={{ xs: 4, sm: 6 }}>
                    <Grid item xs={12} md={8}>
                        <Paper elevation={4} sx={{ p: { xs: 3, sm: 4 }, borderRadius: { xs: 3, sm: 4 } }}>
                            <Typography
                                variant="h4"
                                fontWeight={700}
                                gutterBottom
                                color="#1b5e20"
                                sx={{
                                    fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' },
                                    lineHeight: { xs: 1.2, sm: 1.3 }
                                }}
                            >
                                📝 Send us a Message
                            </Typography>
                            <Typography
                                variant="body1"
                                paragraph
                                color="#546e7a"
                                sx={{
                                    mb: { xs: 3, sm: 4 },
                                    fontSize: { xs: '0.9rem', sm: '1rem' },
                                    lineHeight: { xs: 1.5, sm: 1.6 }
                                }}
                            >
                                Have a question, suggestion, or just want to say hello? Fill out the form below and we'll get back to you as soon as possible.
                            </Typography>

                            {submitted && success && (
                                <Alert severity="success" sx={{ mb: 3, borderRadius: 3 }}>
                                    {success} 🌟
                                </Alert>
                            )}

                            {error && (
                                <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
                                    {error}
                                </Alert>
                            )}

                            <form onSubmit={handleSubmit}>
                                <Grid container spacing={{ xs: 2, sm: 3 }}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Your Name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                            variant="outlined"
                                            size={isSmallMobile ? "small" : "medium"}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Email Address"
                                            name="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                            variant="outlined"
                                            size={isSmallMobile ? "small" : "medium"}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Subject"
                                            name="subject"
                                            value={formData.subject}
                                            onChange={handleChange}
                                            required
                                            variant="outlined"
                                            size={isSmallMobile ? "small" : "medium"}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Your Message"
                                            name="message"
                                            multiline
                                            rows={isMobile ? 4 : 6}
                                            value={formData.message}
                                            onChange={handleChange}
                                            required
                                            variant="outlined"
                                            placeholder="Tell us what's on your mind..."
                                        />
                                    </Grid>
                                    <Grid item xs={12} style={{ display: 'none' }}>
                                        <TextField
                                            fullWidth
                                            label="Website (Leave blank)"
                                            name="website"
                                            value={formData.website}
                                            onChange={handleChange}
                                            variant="outlined"
                                            size={isSmallMobile ? "small" : "medium"}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            color="primary"
                                            size="large"
                                            startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <Send />}
                                            sx={{
                                                fontWeight: 700,
                                                px: { xs: 3, sm: 4 },
                                                py: { xs: 1.2, sm: 1.5 },
                                                fontSize: { xs: '0.9rem', sm: '1rem' },
                                                width: { xs: '100%', sm: 'auto' }
                                            }}
                                            disabled={loading}
                                        >
                                            {loading ? 'Sending...' : 'Send Message 🚀'}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </form>
                        </Paper>
                    </Grid>

                    {/* Support Types - Mobile Responsive */}
                    <Grid item xs={12} md={4}>
                        <Paper
                            elevation={2}
                            sx={{
                                p: { xs: 3, sm: 4 },
                                borderRadius: { xs: 3, sm: 4 },
                                background: 'linear-gradient(135deg, #f5fbf7, #ffffff)',
                                border: '2px solid #43a04710'
                            }}
                        >
                            <Typography
                                variant="h5"
                                fontWeight={700}
                                gutterBottom
                                color="#1b5e20"
                                sx={{
                                    fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.5rem' },
                                    textAlign: { xs: 'center', md: 'left' }
                                }}
                            >
                                💡 How can we help?
                            </Typography>
                            <Stack spacing={{ xs: 2, sm: 3 }}>
                                {supportTypes.map((type, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            p: { xs: 2, sm: 3 },
                                            borderRadius: 3,
                                            background: 'rgba(67, 160, 71, 0.05)',
                                            border: '1px solid rgba(67, 160, 71, 0.1)',
                                            textAlign: { xs: 'center', sm: 'left' },
                                            flexDirection: { xs: 'column', sm: 'row' }
                                        }}
                                    >
                                        <Avatar sx={{
                                            bgcolor: '#43a047',
                                            width: { xs: 40, sm: 45 },
                                            height: { xs: 40, sm: 45 },
                                            mr: { xs: 0, sm: 2 },
                                            mb: { xs: 1, sm: 0 },
                                            mx: { xs: 'auto', sm: 0 }
                                        }}>
                                            {type.icon}
                                        </Avatar>
                                        <Box>
                                            <Typography
                                                variant="h6"
                                                fontWeight={600}
                                                color="#1b5e20"
                                                sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}
                                            >
                                                {type.title}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="#546e7a"
                                                sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}
                                            >
                                                {type.description}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>

                            {/* Developer Contact */}
                            <Box sx={{
                                mt: { xs: 3, sm: 4 },
                                p: { xs: 2, sm: 3 },
                                borderRadius: 3,
                                background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                                color: 'white',
                                textAlign: 'center'
                            }}>
                                <Typography
                                    variant="h6"
                                    fontWeight={700}
                                    gutterBottom
                                    sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}
                                >
                                    🚀 Meet the Developers
                                </Typography>
                                <Typography
                                    variant="body2"
                                    paragraph
                                    sx={{
                                        opacity: 0.9,
                                        fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                    }}
                                >
                                    Connect with Md. Alamin & Saikat Kumar Gain
                                </Typography>
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={1}
                                    justifyContent="center"
                                >
                                    <Button
                                        component={Link}
                                        to="/about"
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                            borderColor: 'white',
                                            color: 'white',
                                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                            '&:hover': {
                                                borderColor: 'white',
                                                backgroundColor: 'rgba(255,255,255,0.1)'
                                            }
                                        }}
                                    >
                                        Learn More
                                    </Button>
                                </Stack>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default ContactUsPage;
